import { MutableRefObject, RefObject, useEffect, useRef } from 'react';
import { getTwoFingerSpan } from '@pages/MediaViewer/AssetViewer/PdfCard/utils';
import { IAssetViewerStateContextValue } from '@context/AssetViewerStateContext';

const minZoom = 0.1;
const maxZoom = 3;

export type ImagePinchGestureState = {
  isActive: boolean;
  startSpan: number;
  baseZoom: number;
  /** Point between the fingers when the pinch started (client coordinates) */
  focalX: number;
  focalY: number;
};

type UseMobileImagePinchZoomParams = {
  enabled: boolean;
  viewportRef: RefObject<HTMLElement | null>;
  imagePinchGestureRef: MutableRefObject<ImagePinchGestureState>;
  zoomFactor: number;
  setZoomFactor: IAssetViewerStateContextValue['setZoomFactor'];
  /**
   * Shows the image at this zoom while pinching (before it is applied),
   * keeping the point between the fingers in place
   */
  onPinchPreview: (zoom: number, focal: { x: number; y: number }) => void;
};

export function useMobileImagePinchZoom({
  enabled,
  viewportRef,
  imagePinchGestureRef,
  zoomFactor,
  setZoomFactor,
  onPinchPreview
}: UseMobileImagePinchZoomParams) {
  const zoomRef = useRef(zoomFactor);
  zoomRef.current = zoomFactor;

  const onPinchPreviewRef = useRef(onPinchPreview);
  onPinchPreviewRef.current = onPinchPreview;

  const pendingPinchZoomRef = useRef<number | null>(null);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!enabled || !viewport) return undefined;

    const pinch = imagePinchGestureRef.current;

    const commitPinchZoom = () => {
      const nextZoom = pendingPinchZoomRef.current;
      pendingPinchZoomRef.current = null;
      if (nextZoom == null || nextZoom === zoomRef.current) return;
      setZoomFactor(nextZoom);
    };

    const onTouchStart = (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (touchEvent.touches.length < 2) return;
      const span = getTwoFingerSpan(touchEvent.touches);
      if (span < 10) return;

      if (!pinch.isActive) {
        pinch.isActive = true;
        pinch.startSpan = span;
        pinch.baseZoom = zoomRef.current;
        pinch.focalX =
          (touchEvent.touches[0].clientX + touchEvent.touches[1].clientX) / 2;
        pinch.focalY =
          (touchEvent.touches[0].clientY + touchEvent.touches[1].clientY) / 2;
        pendingPinchZoomRef.current = null;
      }
    };

    const onTouchMove = (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (!pinch.isActive || touchEvent.touches.length < 2) return;
      const span = getTwoFingerSpan(touchEvent.touches);
      if (pinch.startSpan < 10) return;

      // Large images fit below the min zoom: not zoomed in to it at once
      const nextZoom = Math.min(
        maxZoom,
        Math.max(
          Math.min(minZoom, pinch.baseZoom),
          pinch.baseZoom * (span / pinch.startSpan)
        )
      );

      // Not scrolling meanwhile
      touchEvent.preventDefault();
      pendingPinchZoomRef.current = nextZoom;
      onPinchPreviewRef.current(nextZoom, { x: pinch.focalX, y: pinch.focalY });
    };

    const onTouchEnd = (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (touchEvent.touches.length < 2 && pinch.isActive) {
        pinch.isActive = false;
        commitPinchZoom();
      }
    };

    const listenerCapturePassive = { capture: true, passive: true } as const;
    const listenerCaptureNotPassive = {
      capture: true,
      passive: false
    } as const;

    viewport.addEventListener(
      'touchstart',
      onTouchStart,
      listenerCapturePassive
    );
    viewport.addEventListener(
      'touchmove',
      onTouchMove,
      listenerCaptureNotPassive
    );
    viewport.addEventListener('touchend', onTouchEnd, listenerCapturePassive);
    viewport.addEventListener(
      'touchcancel',
      onTouchEnd,
      listenerCapturePassive
    );

    return () => {
      pinch.isActive = false;
      pendingPinchZoomRef.current = null;
      viewport.removeEventListener(
        'touchstart',
        onTouchStart,
        listenerCapturePassive
      );
      viewport.removeEventListener(
        'touchmove',
        onTouchMove,
        listenerCaptureNotPassive
      );
      viewport.removeEventListener(
        'touchend',
        onTouchEnd,
        listenerCapturePassive
      );
      viewport.removeEventListener(
        'touchcancel',
        onTouchEnd,
        listenerCapturePassive
      );
    };
  }, [enabled, viewportRef, imagePinchGestureRef, setZoomFactor]);
}
